import { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Users, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const GALLERY_PHOTOS = [
  'hooyos-place/gallery/4cd0cfd7-2550-4a66-b40a-779ef5f6abe6.jpeg',
  'hooyos-place/gallery/ea772e4f-6f4b-4a0e-b6ec-2f2eb6b4b48a.jpeg',
  'hooyos-place/gallery/48857230-01ca-4688-b472-793fc3897522.jpeg',
  'hooyos-place/gallery/f927e7f9-29b1-442f-9d7c-cd79ee5cfeae.jpeg',
  'hooyos-place/gallery/c5ece1d5-ae17-411b-a820-a935cf2ff02a.jpeg',
  'hooyos-place/gallery/acf032e3-697a-4ab8-9992-30c4c6c81d14.jpeg',
  'hooyos-place/gallery/0f5fdb2f-f7d8-4600-a78b-842e323ad3fd.jpeg',
];

const timeSlots = [
  { value: 'morning-9', label: '9:00 AM' },
  { value: 'morning-10', label: '10:00 AM' },
  { value: 'morning-11', label: '11:00 AM' },
  { value: 'afternoon-1', label: '1:00 PM' },
  { value: 'afternoon-2', label: '2:00 PM' },
  { value: 'afternoon-3', label: '3:00 PM' },
];

const MobileTour = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    parent_name: '', email: '', phone: '', preferred_date: '', preferred_time: '', num_children: 1, child_ages: '', message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/tour-requests`, formData);
      setSubmitted(true);
      toast.success('Tour request submitted!');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" data-testid="mobile-tour-success">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 15 }}>
          <div className="w-20 h-20 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
        </motion.div>
        <h2 className="font-fredoka font-bold text-xl text-foreground mb-2">Tour Requested!</h2>
        <p className="text-sm text-muted-foreground mb-6">We'll contact you within 24 hours to confirm your visit.</p>
        <Button onClick={() => { setSubmitted(false); setFormData({ parent_name: '', email: '', phone: '', preferred_date: '', preferred_time: '', num_children: 1, child_ages: '', message: '' }); }} variant="outline" className="rounded-xl" data-testid="mobile-tour-reset">
          Schedule Another
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-6" data-testid="mobile-tour-page">
      {/* Photo Strip */}
      <div className="overflow-hidden mb-4">
        <style>{`
          @keyframes mobileMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .mobile-marquee { display: flex; gap: 8px; width: max-content; animation: mobileMarquee 25s linear infinite; }
        `}</style>
        <div className="mobile-marquee" data-testid="mobile-tour-marquee">
          {[...GALLERY_PHOTOS, ...GALLERY_PHOTOS].map((path, i) => (
            <div key={i} className="flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden">
              <img src={`${FILES_BASE}/${path}`} alt="Our space" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="px-4 mb-4 text-center">
        <h2 className="font-fredoka font-bold text-xl text-foreground" data-testid="mobile-tour-heading">Schedule a Tour</h2>
        <p className="text-xs text-muted-foreground mt-1">Come see our warm, welcoming space in person!</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-4 space-y-4">
        <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
          <h3 className="font-fredoka font-semibold text-sm text-foreground flex items-center gap-2">
            <Users className="w-4 h-4 text-secondary" /> Your Info
          </h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Full Name *</Label>
            <Input name="parent_name" required value={formData.parent_name} onChange={handleChange} placeholder="Jane Doe" className="rounded-xl h-10 text-sm" data-testid="mobile-tour-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Phone *</Label>
              <Input name="phone" type="tel" required value={formData.phone} onChange={handleChange} placeholder="(503) 555-1234" className="rounded-xl h-10 text-sm" data-testid="mobile-tour-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="you@email.com" className="rounded-xl h-10 text-sm" data-testid="mobile-tour-email" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
          <h3 className="font-fredoka font-semibold text-sm text-foreground flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-secondary" /> Tour Details
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Date *</Label>
              <Input name="preferred_date" type="date" required value={formData.preferred_date} onChange={handleChange} className="rounded-xl h-10 text-sm" data-testid="mobile-tour-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Time *</Label>
              <Select value={formData.preferred_time} onValueChange={(v) => setFormData(prev => ({ ...prev, preferred_time: v }))}>
                <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-tour-time"><SelectValue placeholder="Pick time" /></SelectTrigger>
                <SelectContent>
                  {timeSlots.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Children</Label>
              <Input name="num_children" type="number" min="1" max="10" value={formData.num_children} onChange={handleChange} className="rounded-xl h-10 text-sm" data-testid="mobile-tour-children" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Age(s)</Label>
              <Input name="child_ages" value={formData.child_ages} onChange={handleChange} placeholder="e.g. 3 and 5" className="rounded-xl h-10 text-sm" data-testid="mobile-tour-ages" />
            </div>
          </div>
        </div>

        <div className="space-y-1.5 px-1">
          <Label className="text-xs">Questions or Requests</Label>
          <Textarea name="message" rows={3} value={formData.message} onChange={handleChange} placeholder="Anything you'd like us to know?" className="rounded-xl text-sm resize-none" data-testid="mobile-tour-message" />
        </div>

        <Button type="submit" disabled={submitting} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-xl py-5 font-bold shadow-soft" data-testid="mobile-tour-submit">
          {submitting ? 'Submitting...' : 'Request a Tour'}
        </Button>

        <div className="bg-success/8 rounded-xl p-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
          <p className="text-xs text-muted-foreground">We'll confirm within 24 hours. Walk-ins also welcome!</p>
        </div>
      </form>
    </div>
  );
};

export default MobileTour;
